import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getItemToken } from "../../../Utils/localStorageUtils";
import AdminCustomInput from "../../../components/CommonComponents/AdminCustomInput";
import { FaSearch, FaUser, FaTimes } from "react-icons/fa";

const UserSelector = ({ 
  selectedUsers = [], 
  onSelectionChange, 
  multiple = true,
  placeholder = "Search and select users...",
  className = "",
  currentPartnerId
}) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const userAccessToken = getItemToken("access_token");
  
  const ITEMS_PER_PAGE = 20;
  const SEARCH_DEBOUNCE_DELAY = 300;

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        fetchUsers(1, searchTerm);
      }
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Fetch users from API
  const fetchUsers = async (page = 1, search = "") => {
    if (loading) return;
    
    setLoading(true);
    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
        search: search.trim(),
        soft_delete: 0 // Only active users
      };

      const response = await axios.get(
        `${process.env.REACT_APP_Baseurl}/admin/users/list`,
        {
          headers: userAccessToken,
          params
        }
      );

      if (response?.data) {
        let newUsers = response.data.data || [];
        const pagination = response.data.pagination || {};
        
        newUsers = newUsers.filter(user => {
          const roleId = user.role_id || user.role?.id || user.user_role || user.role;
          
          // Convert to number for comparison and exclude roles 1 and 5
          const numericRoleId = parseInt(roleId);
          const isNotExcluded = !currentPartnerId.includes(user.partner_id);
          const shouldInclude = numericRoleId !== 1 && numericRoleId !== 5 && isNotExcluded;
          
          return shouldInclude;
        });
        
        if (page === 1) {
          setUsers(newUsers);
          setFilteredUsers(newUsers);
        } else {
          setUsers(prev => [...prev, ...newUsers]);
          setFilteredUsers(prev => [...prev, ...newUsers]);
        }
        
        setCurrentPage(page);
        setTotalPages(pagination.totalPages || Math.ceil(pagination.totalItems / ITEMS_PER_PAGE) || 1);
        setHasMore(page < (pagination.totalPages || Math.ceil(pagination.totalItems / ITEMS_PER_PAGE) || 1));
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Load more users (pagination)
  const loadMoreUsers = () => {
    if (hasMore && !loading) {
      fetchUsers(currentPage + 1, searchTerm);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    if (multiple) {
      const isSelected = selectedUsers.some(selected => selected.id === user.id);
      let newSelection;
      
      if (isSelected) {
        newSelection = selectedUsers.filter(selected => selected.id !== user.id);
      } else {
        newSelection = [...selectedUsers, user];
      }
      
      onSelectionChange(newSelection);
    } else {
      onSelectionChange([user]);
      setIsDropdownOpen(false);
    }
  };

  // Remove selected user
  const removeSelectedUser = (userId) => {
    const newSelection = selectedUsers.filter(user => user.id !== userId);
    onSelectionChange(newSelection);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initial load
  useEffect(() => {
    fetchUsers(1);
  }, []);

  const getUserDisplayName = (user) => {
    return `${user.username || user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
  };

  const getUserRole = (user) => {
    return user.role_name || user.role || 'User';
  };

  return (
    <div className={`user-selector ${className}`} ref={dropdownRef}>
      {/* Selected Users Display */}
      {selectedUsers.length > 0 && (
        <div className="selected-users-container mb-3">
          <div className="selected-users-list">
            {selectedUsers.map(user => (
              <div key={user.id} className="selected-user-item">
                <div className="user-info">
                  <FaUser className="user-icon" />
                  <span className="user-name">{getUserDisplayName(user)}</span>
                  <span className="user-role">({getUserRole(user)})</span>
                </div>
                <button
                  type="button"
                  className="remove-user-btn"
                  onClick={() => removeSelectedUser(user.id)}
                  title="Remove user"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="search-container position-relative">
        <AdminCustomInput
          type="text"
          ref={searchRef}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setIsDropdownOpen(true)}
          placeHolder={placeholder}
          className="search-input"
        />
        <FaSearch className="search-icon position-absolute" />
      </div>

      {/* Dropdown List */}
      {isDropdownOpen && (
        <div className="users-dropdown">
          <div className="dropdown-header">
            <span className="dropdown-title">
              Available Users ({filteredUsers.length} found)
            </span>
            {loading && <span className="loading-indicator">Loading...</span>}
          </div>
          
          <div className="users-list">
            {filteredUsers.length === 0 && !loading ? (
              <div className="no-users-message">
                {searchTerm ? 
                  `No users found matching "${searchTerm}"` : 
                  "No users available"
                }
              </div>
            ) : (
              filteredUsers.map(user => {
                const isSelected = selectedUsers.some(selected => selected.id === user.id);
                return (
                  <div
                    key={user.id}
                    className={`user-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleUserSelect(user)}
                  >
                    <div className="user-avatar">
                      {user.avatar ? (
                        <img src={user.avatar} alt={getUserDisplayName(user)} />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                    <div className="user-details">
                      <div className="user-name">{getUserDisplayName(user)}</div>
                      <div className="user-meta">
                        <span className="user-email">{user.email}</span>
                        <span className="user-role">• {getUserRole(user)}</span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="selection-indicator">✓</div>
                    )}
                  </div>
                );
              })
            )}
            
            {/* Load More Button */}
            {hasMore && !loading && filteredUsers.length > 0 && (
              <div className="load-more-container">
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={loadMoreUsers}
                >
                  Load More Users
                </button>
              </div>
            )}
            
            {loading && filteredUsers.length > 0 && (
              <div className="loading-more">Loading more users...</div>
            )}
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .user-selector {
          position: relative;
          width: 100%;
        }

        .selected-users-container {
          margin-bottom: 15px;
        }

        .selected-users-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .selected-user-item {
          display: flex;
          align-items: center;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 14px;
        }

        .selected-user-item .user-info {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .selected-user-item .user-icon {
          color: #6c757d;
          font-size: 12px;
        }

        .selected-user-item .user-name {
          font-weight: 500;
        }

        .selected-user-item .user-role {
          color: #6c757d;
          font-size: 12px;
        }

        .remove-user-btn {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          margin-left: 8px;
          padding: 2px;
          border-radius: 3px;
        }

        .remove-user-btn:hover {
          background: #f8d7da;
        }

        .search-container {
          position: relative;
        }

        .search-icon {
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          pointer-events: none;
        }

        .users-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          max-height: 400px;
          overflow-y: auto;
        }

        .dropdown-header {
          padding: 12px 15px;
          border-bottom: 1px solid #dee2e6;
          background: #f8f9fa;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dropdown-title {
          font-weight: 500;
          font-size: 14px;
          color: #495057;
        }

        .loading-indicator {
          font-size: 12px;
          color: #6c757d;
        }

        .users-list {
          max-height: 320px;
          overflow-y: auto;
        }

        .user-item {
          display: flex;
          align-items: center;
          padding: 12px 15px;
          cursor: pointer;
          border-bottom: 1px solid #f8f9fa;
          transition: background-color 0.2s;
        }

        .user-item:hover {
          background: #f8f9fa;
        }

        .user-item.selected {
          background: #e7f3ff;
          border-left: 3px solid #007bff;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #dee2e6;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          overflow: hidden;
        }

        .user-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-avatar svg {
          color: #6c757d;
          font-size: 16px;
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-weight: 500;
          font-size: 14px;
          color: #212529;
          margin-bottom: 2px;
        }

        .user-meta {
          font-size: 12px;
          color: #6c757d;
        }

        .user-email {
          margin-right: 8px;
        }

        .selection-indicator {
          color: #28a745;
          font-weight: bold;
          font-size: 16px;
        }

        .no-users-message {
          padding: 20px;
          text-align: center;
          color: #6c757d;
          font-style: italic;
        }

        .load-more-container {
          padding: 12px;
          text-align: center;
          border-top: 1px solid #f8f9fa;
        }

        .load-more-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .load-more-btn:hover {
          background: #0056b3;
        }

        .loading-more {
          padding: 12px;
          text-align: center;
          color: #6c757d;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default UserSelector;
